import { useCallback, useImperativeHandle, useMemo, useRef, useState, type Ref, type RefObject } from 'react'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useAccounts } from '../accounts/useAccounts'
import { useCategories } from '../categories/useCategories'
import { useMerchantNames } from '../merchants/useMerchants'
import { BackfillConfirm } from './BackfillConfirm'
import { blankDraft } from './builderState'
import { RuleBuilder } from './RuleBuilder'
import type { AsyncState } from '../../platform/async/Async'
import type { ReadRule, RuleDraft } from './ruleWire'
import type { Names } from './ruleText'
import { useRuleCap } from './ruleCap'
import { useRules } from './useRules'
import { SaveError, useSaveRule } from './useSaveRule'

/** The count behind a draft, for a caller that builds one (see `./draft` for
 *  the pure half of this module's public face). */
export { useUnsavedPreview } from './useRules'
/** Which rule set a field on a transaction, and where its page is: for the transaction detail. */
export { useRuleThatSet, type SetByRule } from './useRuleLogs'
export { rulePath } from './rulePaths'
/** Whether a new rule may be made; read by every screen that offers one. */
export { useRuleCap } from './ruleCap'

/** The dry run's status text — the switch's, under the list, and the
 *  builder's, beside its Save. */
export const COUNTING = 'Counting past transactions…'

/** What revert really does: it touches only what the rule wrote, and a field changed since, or set
 *  by the user, is left (`skipped_changed`, `skipped_locked`). Never "exactly as they were". */
const UNDO_BODY = 'Undo puts back the values these fields had before the rule ran. Anything changed since, or set by you, stays as it is.'

/** Name lookups for `ruleText`. Never blocks the list: while categories or
 *  accounts load, or fail, the lookups return undefined and the summary
 *  falls back to its "(deleted …)" wording.
 *
 *  Merchants are read by id, so the caller names the ids it will ask about:
 *  every "Set merchant" value in the list. A merchant reads "a merchant" while
 *  it resolves and "Unknown merchant" once the server says the id names none. */
export function useNames(merchantIds: readonly string[]): Names {
  const categories = useCategories()
  const accounts = useAccounts()
  const merchant = useMerchantNames(merchantIds)
  const cats = categories.status === 'success' || categories.status === 'stale' ? categories.data : undefined
  const categoriesLoading = categories.status === 'loading'
  const accs = accounts.status === 'success' || accounts.status === 'stale' ? accounts.data : undefined
  return useMemo(
    () => ({
      category: (id) => cats?.find((c) => c.id === id)?.name,
      categoriesLoading,
      account: (id) => accs?.find((a) => a.id === id)?.name,
      merchant: (id) => {
        const m = merchant(id)
        return m.status === 'found' ? { status: 'found', name: m.merchant.name } : m
      },
    }),
    [cats, categoriesLoading, accs, merchant],
  )
}

/** Every "Set merchant" value across the rules, for `useNames`. */
function merchantIdsOf(rules: AsyncState<ReadRule[]>): string[] {
  if (rules.status !== 'success' && rules.status !== 'stale') return []
  return rules.data.flatMap((r) => (r.editable ? r.draft.actions.filter((a) => a.type === 'set_merchant').map((a) => a.value) : []))
}

/**
 * What a caller outside this module may do: open the builder on a draft, or
 * save one without opening it. Each is a new save session and `after` runs only
 * once the rule is FULLY saved, any confirmed backfill included.
 */
/** How a direct save session ended. `declined` and `count_failed` both mean the rule
 *  EXISTS, so a caller must not offer to create it again, but they differ in what the
 *  past holds: `declined`, the user chose not to apply it (Back, Escape, scrim, or a
 *  superseded session), so nothing was changed; `count_failed`, the backfill count
 *  failed, so what it would have changed is unknown. `failed`: nothing was written. */
export type SaveOutcome = 'done' | 'declined' | 'count_failed' | 'failed'

export type RuleFlowHandle = {
  openBuilder(draft: RuleDraft, after?: () => void): void
  /**
   * `onWrittenUncounted` runs when the rule WAS written but the backfill count
   * then failed: the rule exists, `after` will not run (the save is not fully
   * done), and the caller must not offer to create it again.
   *
   * Resolves when the save SESSION ends, including the backfill confirmation,
   * so a caller can hold its button for the whole of it. A failure is already
   * toasted.
   */
  saveDirect(draft: RuleDraft, after?: () => void, onWrittenUncounted?: () => void): Promise<SaveOutcome>
}

type Options = {
  /** The builder's Delete, for an existing rule. Absent, the builder offers none. */
  onDelete?: (read: ReadRule) => void
  /** Where the builder returns focus when its opener is gone (#504). */
  restoreFocusTo?: RefObject<HTMLElement | null>
}

/**
 * The whole rule-writing flow — builder, the save with its count and its
 * backfill confirmation, the switch, and Undo — extracted from `Rules`
 * unchanged so a second screen can open the same builder (M6's bulk "Create
 * rule from these", and "Make it a rule" on a transaction).
 *
 * **A hook as well as a component, because `Rules` reads the saver's own state.**
 * Its list needs `switching` (which rows have a flip in flight), `counting` (the
 * status line under the list) and the pause/enable it drives from each row's
 * switch. A ref handle cannot expose reactive state to its parent's render, so
 * `Rules` uses the hook and renders `element` itself. `RuleFlow` below is the
 * same hook behind an imperative handle, for callers that only open it.
 *
 * Nothing here is new behaviour: the bodies of `openBuilder`, `submit` and the
 * `onDone` chain are what `Rules` had, moved.
 */
export function useRuleFlow({ onDelete, restoreFocusTo }: Options = {}) {
  const state = useRules()
  const names = useNames(merchantIdsOf(state))
  const cap = useRuleCap()
  const toast = useToast()
  const [editing, setEditing] = useState<ReadRule | 'new' | null>(null)
  /** The builder opened with a prefill, and what to do once the rule is FULLY
   *  saved (after any confirmed backfill). */
  const [prefill, setPrefill] = useState<RuleDraft | null>(null)
  /** Why the prefill differs from what the caller asked for, shown in the builder. */
  const [notice, setNotice] = useState<string | null>(null)
  const onSaved = useRef<(() => void) | null>(null)
  /** Settles the pending `saveDirect`, if any. */
  const direct = useRef<((o: SaveOutcome) => void) | null>(null)
  const settle = (o: SaveOutcome) => {
    const d = direct.current
    direct.current = null
    d?.(o)
  }
  const closeBuilder = useCallback(() => {
    setEditing(null)
    setPrefill(null)
  }, [])
  // The whole save — builder and switch — including the count, the
  // confirmation and Undo. See useSaveRule. `onDone` runs only when a save
  // completes; cancel, "Back to rule" and a failure never reach it.
  const saver = useSaveRule({
    onDone: () => {
      const after = onSaved.current
      onSaved.current = null
      closeBuilder()
      after?.()
      settle('done')
    },
  })

  /** Every way a builder opens. Each is a new save session: nothing an
   *  earlier builder wrote carries into this one (see useSaveRule `begin`). */
  const open = (
    target: ReadRule | 'new',
    draft: RuleDraft | null = null,
    after: (() => void) | null = null,
    extra: { onWrittenUncounted?: () => void; notice?: string } = {},
  ) => {
    // Refused first: a builder that never opens has not superseded anything,
    // so a direct save still waiting on its answer must not be settled by it.
    if (target === 'new' && cap.atCap) {
      toast.show({ kind: 'error', message: cap.reason })
      return
    }
    settle('declined')
    saver.begin(extra.onWrittenUncounted)
    setNotice(extra.notice ?? null)
    onSaved.current = after
    setPrefill(draft)
    setEditing(target)
  }

  const saveDirect = (draft: RuleDraft, after?: () => void, onWrittenUncounted?: () => void): Promise<SaveOutcome> => {
    settle('declined')
    saver.begin()
    onSaved.current = after ?? null
    return new Promise<SaveOutcome>((resolve) => {
      direct.current = resolve
      // No builder is open, so nothing else would tell the user a write failed.
      saver.save(draft, { from: null }).then(
        // Resolved with the confirmation up: the session is still open, and
        // `onDone` or `leaveDirect` settles it.
        () => undefined,
        (e: unknown) => {
          if (!(e instanceof SaveError)) {
            direct.current = null
            throw e
          }
          toast.show({ kind: 'error', message: e.message })
          if (e.saved) onWrittenUncounted?.()
          settle(e.saved ? 'count_failed' : 'failed')
        },
      )
    })
  }

  /** Back or Escape on the confirmation of a direct save: there is no builder to
   *  go back to, so this is the builder closing after its write. */
  const leaveDirect = () => {
    if (saver.step.k === 'applying') return
    saver.close()
    onSaved.current = null
    settle('declined')
  }

  const submit = (draft: RuleDraft, extra: { priority?: number; renumber?: boolean }) =>
    saver.save(draft, { ...extra, from: editing === 'new' ? null : editing })

  const all = state.status === 'success' || state.status === 'stale' ? state.data : undefined
  // Read-only rules never open: Task 5 hides their Edit.
  const initial = editing === 'new' ? (prefill ?? blankDraft()) : editing?.editable ? editing.draft : null

  const element = (
    <>
      {editing !== null && initial !== null && (
        <RuleBuilder
          initial={initial}
          notice={editing === 'new' ? (notice ?? undefined) : undefined}
          priorities={all?.map((r) => r.rule.priority) ?? []}
          onClose={() => {
            saver.close()
            onSaved.current = null
            closeBuilder()
          }}
          onSubmit={submit}
          restoreFocusTo={restoreFocusTo}
          progress={saver.step.k === 'counting' ? { button: 'Counting…', status: COUNTING } : undefined}
          // The builder holds itself open through its own save and count; the
          // flip after a confirmation runs outside it, so it is told.
          busy={saver.step.k === 'applying'}
          onDelete={editing === 'new' || onDelete === undefined ? undefined : () => onDelete(editing)}
        />
      )}
      {saver.dialog !== null && (
        <BackfillConfirm
          key={`${saver.dialog.offer.via}:${saver.dialog.offer.id}`}
          offer={saver.dialog.offer}
          phase={
            saver.dialog.step.k === 'failed'
              ? { k: 'failed', message: saver.dialog.step.message }
              : { k: saver.dialog.step.k === 'applying' ? 'applying' : 'asking' }
          }
          categoryName={
            saver.dialog.offer.categoryId === null
              ? null
              : (names.category(saver.dialog.offer.categoryId) ?? 'the chosen category')
          }
          onApply={() => void saver.confirm()}
          onSkip={() => {
            if (direct.current === null || saver.dialog?.offer.via === 'enable') return saver.skipPast()
            // A direct save: skipping IS declining, so it ends as Escape and the scrim do.
            if (saver.dialog?.step.k === 'applying') return
            saver.skipPast({ asDecline: true })
            onSaved.current = null
            settle('declined')
          }}
          onBack={() =>
            direct.current === null || saver.dialog?.offer.via === 'enable' ? saver.back() : leaveDirect()
          }
          hideBack={direct.current !== null && saver.dialog.offer.via === 'save'}
        />
      )}
      <ConfirmDialog
        open={saver.undo !== null}
        mode="revert_rule_batch"
        title="Undo these changes?"
        body={UNDO_BODY}
        confirmLabel="Undo"
        pendingLabel="Undoing…"
        onCancel={saver.closeUndo}
        onConfirm={saver.revertBatch}
        errorFor={() => "Couldn't undo. Nothing was changed. Try again in a moment."}
      />
    </>
  )

  return {
    state,
    names,
    all,
    saver,
    open,
    saveDirect,
    /** The cap on new rules, for the screen that offers them. */
    cap,
    /** A delete or pause from the builder makes its edit moot. */
    afterDelete: () => {
      saver.reset()
      setEditing(null)
    },
    /** Whether the switch's dry run is counting — the list's status line. */
    counting: saver.switchStep.k === 'counting',
    element,
  }
}

/** `RuleFlow` as a component: for a screen that only opens the builder. */
export function RuleFlow({ ref }: { ref?: Ref<RuleFlowHandle> }) {
  const flow = useRuleFlow()
  useImperativeHandle(ref, () => ({
    openBuilder: (draft, after) => flow.open('new', draft, after ?? null),
    saveDirect: flow.saveDirect,
  }))
  return flow.element
}
